import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { TwilioMessageModel, useTwilioMessages } from "@jugl-web/rest-api";
import { DataLoadingWrapper, LoadingAnimation } from "@jugl-web/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { VList, VListHandle } from "virtua";
import useDebounce from "@web-src/hooks/useDebounce";
import { isoToLocalDate, useTranslations } from "@jugl-web/utils";
import { format, isThisYear, isToday } from "date-fns";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { ChatDateHeader } from "@web-src/modules/chats/pages/ChatsPage/components/ChatWindow/components/ChatMessagesWindow/components/ChatDateHeader";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ReactComponent as ChevronsDownIcon } from "../WhatsappChatMessageWindow/assets/chevrons-down.svg";
import { TwilioMessageItem } from "../TwilioMessageItem";

export interface TwilioChatMessageWindowHandle {
  scrollToBottom: () => void;
  addMessage: (
    message: TwilioMessageModel,
    forceScrollToBottom?: boolean
  ) => void;
}

type TwilioChatMessageItem =
  | { type: "message"; value: TwilioMessageModel }
  | { type: "date"; value: string };

export const TwilioChatMessageWindow = forwardRef<
  TwilioChatMessageWindowHandle,
  { conversationId: string }
>(({ conversationId }, ref) => {
  const { entityId } = useEntitySelectedProvider();
  const vListRef = useRef<VListHandle>(null);
  const [atBottom, setAtBottom] = useState(true);
  const debouncedAtBottom = useDebounce(atBottom, 500);
  const { incomingTwilioMessages$ } = useClientSupportPageContext();
  const { dateLocale } = useLanguage();
  const { t } = useTranslations();
  const prevLenRef = useRef(0);
  const prevScrollOffsetRef = useRef(0);
  const wasLoadingMore = useRef(false);

  const [isInitialScrollInitialized, setIsInitialScrollInitialized] =
    useState(false);
  const isInitialScrollInitializationInProgress = useRef(false);

  const {
    data: loadedMessages,
    isLoading,
    isLoadingMore,
    isError,
    hasMore,
    loadMore,
    reset,
  } = useTwilioMessages({
    entityId,
    conversationId,
    pageSize: 50,
  });

  const twilioMessageItems = useMemo(() => {
    if (!loadedMessages.length) return [];

    const result: TwilioChatMessageItem[] = [];
    const addedDates: string[] = [];

    loadedMessages.forEach((message) => {
      const date = isoToLocalDate(message.payload.timestamp);
      const dateStrAlt = format(date, "yyyy-MM-dd");

      if (!addedDates.includes(dateStrAlt)) {
        addedDates.push(dateStrAlt);
        const dateStr = isToday(date)
          ? t({
              id: "common.today",
              defaultMessage: "Today",
            })
          : format(date, isThisYear(date) ? "d MMMM" : "d MMMM yyyy", {
              locale: dateLocale,
            });

        result.push({ type: "date", value: dateStr });
      }

      result.push({ type: "message", value: message });
    });

    return result;
  }, [loadedMessages, t, dateLocale]);

  // Reset when conversation changes
  useEffect(() => {
    setIsInitialScrollInitialized(false);
    reset();
  }, [conversationId, reset]);

  const scrollToBottom = useCallback(() => {
    vListRef.current?.scrollToIndex(twilioMessageItems.length, {
      align: "end",
      // LOOKS LIKE SMOOTH SCROLLING IS CURRENTLY BUGGED IN VIRTUA
      // https://github.com/inokawa/virtua/issues/715
    });
    setAtBottom(true);
  }, [twilioMessageItems.length]);

  // Subscribe to incoming messages
  useEffect(() => {
    const subscription = incomingTwilioMessages$.subscribe((message) => {
      if (message.conv_id !== conversationId) return;

      if (atBottom) {
        setTimeout(scrollToBottom, 100);
      }
    });

    return () => subscription.unsubscribe();
  }, [
    incomingTwilioMessages$,
    conversationId,
    atBottom,
    scrollToBottom,
    twilioMessageItems.length,
  ]);

  // Initial scroll to bottom
  useEffect(() => {
    if (
      !isInitialScrollInitialized &&
      !isInitialScrollInitializationInProgress.current &&
      twilioMessageItems.length > 0 &&
      !isLoading
    ) {
      isInitialScrollInitializationInProgress.current = true;
      setTimeout(() => {
        vListRef.current?.scrollToIndex(twilioMessageItems.length - 1, {
          align: "end",
        });
        setIsInitialScrollInitialized(true);
        isInitialScrollInitializationInProgress.current = false;
      }, 100);
    }
  }, [
    twilioMessageItems.length,
    isLoading,
    isInitialScrollInitialized,
    scrollToBottom,
  ]);

  const addMessage = useCallback(
    (message: TwilioMessageModel, forceScrollToBottom?: boolean) => {
      if (forceScrollToBottom || atBottom) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    },
    [atBottom, scrollToBottom]
  );

  useImperativeHandle(ref, () => ({
    scrollToBottom,
    addMessage,
  }));

  const handleLoadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore) {
      prevLenRef.current = twilioMessageItems.length;
      prevScrollOffsetRef.current = vListRef.current?.scrollOffset ?? 0;
      wasLoadingMore.current = true;
      await loadMore();
    }
  }, [hasMore, isLoadingMore, loadMore, twilioMessageItems.length]);

  useEffect(() => {
    if (isLoadingMore) {
      wasLoadingMore.current = true;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (
      wasLoadingMore.current &&
      !isLoadingMore &&
      twilioMessageItems.length > prevLenRef.current
    ) {
      vListRef.current?.scrollToIndex(
        twilioMessageItems.length - prevLenRef.current,
        {
          align: "start",
          offset: prevScrollOffsetRef.current,
        }
      );
      wasLoadingMore.current = false;
    }
    prevLenRef.current = twilioMessageItems.length;
  }, [twilioMessageItems, isLoadingMore]);

  return (
    <div className="relative flex h-full flex-col bg-gray-100">
      <DataLoadingWrapper
        isError={isError}
        isFetching={false}
        isLoading={isLoading && loadedMessages.length === 0}
        onRetry={reset}
      >
        <VList
          ref={vListRef}
          reverse
          className="flex-1 pb-6"
          onScroll={(offset) => {
            if (
              !vListRef.current ||
              isLoadingMore ||
              !isInitialScrollInitialized
            ) {
              return;
            }
            const bottomOffset =
              offset -
              vListRef.current.scrollSize +
              vListRef.current.viewportSize;

            setAtBottom(bottomOffset > -5);
            // Load more messages when scrolling up
            if (offset < 150 && hasMore) {
              handleLoadMore();
            }
          }}
        >
          {isLoading && loadedMessages.length > 0 && hasMore && (
            <div className="flex justify-center py-4">
              <LoadingAnimation size="sm" />
            </div>
          )}
          {twilioMessageItems.map((item, index) => {
            if (item.type === "date") {
              return (
                <ChatDateHeader key={`date-${+index}`} date={item.value} />
              );
            }

            return (
              <TwilioMessageItem
                key={item.value.id}
                message={item.value}
                messages={loadedMessages}
              />
            );
          })}
        </VList>
      </DataLoadingWrapper>

      {!debouncedAtBottom && (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className="bg-primary-700 absolute bottom-4 right-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full shadow-lg transition-opacity hover:opacity-90"
        >
          <ChevronsDownIcon className="text-white" />
        </button>
      )}
    </div>
  );
});

TwilioChatMessageWindow.displayName = "TwilioChatMessageWindow";
